import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobProfileTaskTransport } from '~/shared/data-contract/profile-task';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from '../../api/query-keys';
import type { ProfileTaskParentContext } from '../model/profile-task';

/**
 * Generates a task from its title. The parent item and its versions are invalidated before
 * onSuccess runs, so the caller can open the new task against fresh parent data.
 */
export const useGenerateProfileTask = ({
    context,
    language,
    onError,
    onSuccess,
}: {
    readonly context: ProfileTaskParentContext;
    readonly language: LanguageCodeForJSA;
    readonly onError: (error: Error) => void;
    readonly onSuccess: (created: JobProfileTaskTransport) => void;
}) => {
    const queryClient = useQueryClient();
    const { generateProfileTask } = useApplicationFetchers();

    return useMutation({
        mutationFn: async (title: string) => {
            const result = await generateProfileTask({
                acceptLanguage: language,
                architectureId: context.architectureId,
                itemId: context.parentId,
                title,
            });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        onError,
        onSuccess: async (created) => {
            const invalidations =
                context.parentType === 'role'
                    ? [
                          jobArchitectureKeys.roleItem(context.architectureId, context.parentId),
                          jobArchitectureKeys.roleVersions(context.architectureId, context.parentId),
                      ]
                    : [
                          jobArchitectureKeys.levelItem(context.architectureId, context.parentId),
                          jobArchitectureKeys.levelVersions(context.architectureId, context.parentId),
                      ];
            await Promise.all(invalidations.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
            onSuccess(created);
        },
    });
};
